import { tx } from "@/i18n/tx";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, API_BASE, fmtCHF } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, LogOut, Building2, Calendar, Receipt, Loader2, Mail, Landmark } from "lucide-react";
import PushToggle from "@/components/PushToggle";
import { FEATURES } from "@/lib/features";
import SubscriptionCard from "@/components/plus/SubscriptionCard";
import ClosuresCard from "@/components/plus/ClosuresCard";
import EstablishmentCard from "@/components/plus/EstablishmentCard";
import FiduciaryAccessCard from "@/components/plus/FiduciaryAccessCard";
import { MonthlyReportCard, AccountDataCard } from "@/components/plus/AccountCards";

// Helper to get quarter dates (Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec)
const quarterRange = (year, q) => {
  const startMonth = (q - 1) * 3;
  const start = new Date(Date.UTC(year, startMonth, 1));
  const end = new Date(Date.UTC(year, startMonth + 3, 0));
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};

export default function Plus() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const paywall = params.get("paywall") === "1";

  useEffect(() => {
    if (paywall) toast.error(tx("ui_plus.k1843b0"));
  }, [paywall]);

  // default range = current month
  const todayStr = new Date().toISOString().slice(0, 10);
  const firstOfMonth = new Date();
  firstOfMonth.setDate(1);
  const [start, setStart] = useState(firstOfMonth.toISOString().slice(0, 10));
  const [end, setEnd] = useState(todayStr);

  const buildQuery = () => {
    const p = new URLSearchParams();
    if (start) p.append("start", start);
    if (end) p.append("end", end);
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const dl = (path, filename) => {
    fetch(`${API_BASE}${path}${buildQuery()}`, {
      credentials: "include",
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("download failed");
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
        toast.success(tx("ui_plus.k86d31c"));
      })
      .catch(() => toast.error(tx("ui_plus.kf91b43")));
  };

  const presets = [
    { l: tx("ui_plus.k7e345c"), fn: () => { const f = new Date(); f.setDate(1); setStart(f.toISOString().slice(0,10)); setEnd(todayStr); } },
    { l: tx("ui_plus.kbdf1e8"), fn: () => { const f = new Date(); f.setMonth(f.getMonth()-1); f.setDate(1); const e = new Date(); e.setDate(0); setStart(f.toISOString().slice(0,10)); setEnd(e.toISOString().slice(0,10)); } },
    { l: tx("ui_plus.kb2efac"), fn: () => { const f = new Date(); f.setMonth(f.getMonth()-3); setStart(f.toISOString().slice(0,10)); setEnd(todayStr); } },
    { l: tx("ui_plus.k4525a5"), fn: () => { setStart(""); setEnd(""); } },
  ];

  // ---- Déclaration TVA ----
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const currentQuarter = Math.floor(currentMonth / 3) + 1;
  const defaultVatQ = currentQuarter === 1 ? { year: currentYear - 1, q: 4 } : { year: currentYear, q: currentQuarter - 1 };
  const [vatYear, setVatYear] = useState(defaultVatQ.year);
  const [vatQ, setVatQ] = useState(defaultVatQ.q);
  const [vatCustom, setVatCustom] = useState(false);
  const [vatStart, setVatStart] = useState("");
  const [vatEnd, setVatEnd] = useState("");
  const [vatData, setVatData] = useState(null);
  const [vatLoading, setVatLoading] = useState(false);

  useEffect(() => {
    if (!vatCustom) {
      const r = quarterRange(vatYear, vatQ);
      setVatStart(r.start);
      setVatEnd(r.end);
    }
  }, [vatYear, vatQ, vatCustom]);

  useEffect(() => {
    if (!vatStart || !vatEnd) return;
    let cancelled = false;
    setVatLoading(true);
    api.get(`/reports/vat-declaration?start=${vatStart}&end=${vatEnd}`)
      .then((r) => { if (!cancelled) setVatData(r.data); })
      .catch(() => { if (!cancelled) setVatData(null); })
      .finally(() => { if (!cancelled) setVatLoading(false); });
    return () => { cancelled = true; };
  }, [vatStart, vatEnd]);

  const downloadVat = (fmt) => {
    if (!vatStart || !vatEnd) { toast.error(tx("ui_plus.k7bb6b5")); return; }
    const ext = fmt === "pdf" ? "pdf" : "csv";
    fetch(`${API_BASE}/reports/vat-declaration.${ext}?start=${vatStart}&end=${vatEnd}`, { credentials: "include" })
      .then(async (r) => {
        if (!r.ok) throw new Error("download failed");
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `oresto-declaration-tva-${vatStart}-${vatEnd}.${ext}`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(tx("ui_plus.k86d31c"));
      })
      .catch(() => toast.error(tx("ui_plus.kf91b43")));
  };

  const doLogout = async () => { await logout(); navigate("/"); };
  const isAdmin = user?.role === "admin" || user?.lifetime_access;

  return (
    <div className="space-y-5 pb-4">
      <h1 className="font-display text-3xl">{tx("ui_plus.k32ba78")}</h1>

      {(user?.role === "admin" || user?.lifetime_access) && (
        <Link to="/app/admin" data-testid="admin-link" className="card-c block bg-gradient-to-br from-primary-c to-[#1F5E47] text-white hover:shadow-xl transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-white/70 font-semibold">{tx("ui_plus.k0e6c55")}</p>
              <p className="font-display text-xl mt-1">{tx("ui_plus.k820cdc")}</p>
              <p className="text-xs text-white/80 mt-1">{tx("ui_plus.ka1d789")}</p>
            </div>
            <span className="text-2xl">→</span>
          </div>
        </Link>
      )}

      <SubscriptionCard paywall={paywall} isAdmin={isAdmin} />

      {/* Déclaration TVA */}
      <div className="card-c" data-testid="vat-declaration-card">
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="w-5 h-5 text-secondary-c" />
          <div>
            <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{tx("ui_plus.kc66fdb")}</p>
            <p className="text-[11px] text-text-secondary">{tx("ui_plus.kf6d0c7")}</p>
          </div>
        </div>

        {/* Sélecteur trimestre */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="text-xs text-text-secondary block mb-1">{tx("ui_plus.k8863a5")}</label>
            <select
              data-testid="vat-year-select"
              value={vatYear}
              onChange={(e) => { setVatCustom(false); setVatYear(parseInt(e.target.value)); }}
              className="input-c text-sm"
            >
              {[currentYear, currentYear - 1, currentYear - 2].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-text-secondary block mb-1">{tx("ui_plus.kb2efac")}</label>
            <div className="grid grid-cols-4 gap-1">
              {[1, 2, 3, 4].map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => { setVatCustom(false); setVatQ(q); }}
                  data-testid={`vat-q-${q}`}
                  className={`py-2 text-xs font-semibold rounded-lg ${!vatCustom && vatQ === q ? "bg-secondary-c text-white" : "bg-surface-muted text-text-secondary"}`}
                >
                  T{q}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Custom range toggle */}
        <div className="mb-3">
          <button
            type="button"
            onClick={() => setVatCustom(!vatCustom)}
            data-testid="vat-custom-toggle"
            className="text-xs text-primary-c font-semibold"
          >
            {vatCustom ? tx("ui_plus.k29dbeb") : tx("ui_plus.k1cd437")}
          </button>
          {vatCustom && (
            <div className="grid grid-cols-2 gap-2 mt-2" data-testid="vat-custom-inputs">
              <input type="date" value={vatStart} onChange={(e) => setVatStart(e.target.value)} className="input-c text-sm" data-testid="vat-start-input" />
              <input type="date" value={vatEnd} onChange={(e) => setVatEnd(e.target.value)} className="input-c text-sm" data-testid="vat-end-input" />
            </div>
          )}
        </div>

        {/* Récap visuel */}
        <div className="relative rounded-2xl p-4 bg-gradient-to-br from-white to-[#EFF6F1] border border-primary-c/20" data-testid="vat-summary">
          {vatLoading && (
            <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-2xl z-10">
              <Loader2 className="w-5 h-5 animate-spin text-primary-c" />
            </div>
          )}
          {vatData ? (
            <>
              <p className="text-[11px] uppercase tracking-wider font-semibold text-text-secondary">
                Période {vatStart} → {vatEnd}
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[11px] text-text-secondary">{vatData.vat_method === "tdfn" ? tx("ui_plus.kac2ead") : tx("ui_plus.kb4809b")}</p>
                  <p className="font-display text-lg text-primary-c" data-testid="vat-collected">{fmtCHF(vatData.tva_collectee_total)}</p>
                  <p className="text-[10px] text-text-secondary mt-0.5">
                    {vatData.vat_method === "tdfn"
                      ? `${vatData.tdfn_rate_1}% : ${fmtCHF(vatData.tdfn_due_sur_place)} · ${vatData.tdfn_rate_2}% : ${fmtCHF(vatData.tdfn_due_emporter)}`
                      : `8.1% : ${fmtCHF(vatData.tva_collectee_sur_place)} · 2.6% : ${fmtCHF(vatData.tva_collectee_emporter)}`}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-text-secondary">{tx("ui_plus.k50e9aa")}</p>
                  <p className="font-display text-lg text-secondary-c" data-testid="vat-deductible">{vatData.vat_method === "tdfn" ? tx("ui_plus.k38f725") : `− ${fmtCHF(vatData.tva_deductible_total)}`}</p>
                  <p className="text-[10px] text-text-secondary mt-0.5">
                    {tx("plural.paid_invoices", { count: vatData.nb_factures_payees })}
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-primary-c/20">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-text-secondary">
                  {vatData.tva_nette_a_payer >= 0 ? tx("ui_plus.ka65670") : tx("ui_plus.k9cb323")}
                </p>
                <p
                  className={`font-display text-3xl mt-1 ${vatData.tva_nette_a_payer >= 0 ? "text-[var(--text)]" : "text-primary-c"}`}
                  data-testid="vat-net"
                >
                  {fmtCHF(Math.abs(vatData.tva_nette_a_payer))}
                </p>
                <p className="text-[10px] text-text-secondary mt-1">
                  CA de la période : {fmtCHF(vatData.ca_total_ttc)} · {vatData.nb_caisses} caisse{vatData.nb_caisses > 1 ? "s" : ""}
                </p>
              </div>
            </>
          ) : (
            <p className="text-sm text-text-secondary py-4 text-center">{tx("ui_plus.kea204d")}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 mt-3">
          <button
            data-testid="dl-vat-pdf"
            onClick={() => downloadVat("pdf")}
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-secondary-c text-white text-sm font-semibold"
          >
            <FileText className="w-4 h-4" />PDF
          </button>
          <button
            data-testid="dl-vat-csv"
            onClick={() => downloadVat("csv")}
            className="flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary-c text-white text-sm font-semibold"
          >
            <FileSpreadsheet className="w-4 h-4" />CSV
          </button>
        </div>
      </div>

      {/* Rapports avec filtre de dates */}
      <div className="card-c">
        <div className="flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-primary-c" />
          <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold">{tx("ui_plus.kcf6dbe")}</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-secondary mb-1">{tx("ui_plus.ke76cf8")}</label>
            <input data-testid="export-start-input" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input-c text-sm" />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">{tx("ui_plus.k515322")}</label>
            <input data-testid="export-end-input" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input-c text-sm" />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          {presets.map((p) => (
            <button key={p.l} data-testid={`preset-${p.l}`} onClick={p.fn}
              className="px-3 py-1 text-xs font-semibold rounded-full bg-surface-muted text-text-secondary hover:bg-[#ece8da]">
              {p.l}
            </button>
          ))}
        </div>

        <p className="text-xs text-text-secondary mt-4 mb-2 font-semibold">{tx("ui_plus.k93f063")}</p>
        <div className="grid grid-cols-1 gap-2">
          <button
            data-testid="send-to-fiduciary-btn"
            onClick={async () => {
              const suggested = user?.establishment?.fiduciary_email || "";
              const email = window.prompt("Email de votre fiduciaire :", suggested);
              if (!email || !email.includes("@")) return;
              const note = window.prompt("Ajouter un mot pour votre fiduciaire ? (optionnel)", "") || "";
              try {
                await api.post("/reports/send-to-fiduciary", { email, start, end, note });
                toast.success(tx("tpl.report_sent", { email }));
              } catch (e) {
                const detail = e?.response?.data?.detail;
                const msg = typeof detail === "string" ? detail : (e?.response?.status === 404 ? tx("ui_plus.k721b51") : tx("ui_plus.k4dee3f"));
                toast.error(msg);
              }
            }}
            className="flex items-center justify-between p-3 bg-primary-c text-white rounded-xl hover:bg-primary-c/90 transition-colors text-left"
          >
            <span className="flex items-center gap-3 min-w-0">
              <Mail className="w-5 h-5 shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold truncate">{tx("ui_plus.k177dba")}</p>
                <p className="text-[11px] text-white/80">{tx("ui_plus.k27904f")}</p>
              </div>
            </span>
            <span className="text-xs shrink-0 ml-2">→</span>
          </button>
          <button data-testid="dl-vat-pdf-period" onClick={() => dl("/reports/vat.pdf", "oresto-tva.pdf")}
            className="flex items-center justify-between p-3 bg-surface-muted rounded-xl hover:bg-[#ece8da] transition-colors text-left">
            <span className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-primary-c shrink-0" />
              <span className="truncate">{tx("ui_plus.k8e5a9e")}</span>
            </span>
            <span className="text-xs text-primary-c font-semibold shrink-0 ml-2">{tx("ui_plus.k294cb2")}</span>
          </button>
          <button data-testid="dl-sales-pdf" onClick={() => dl("/reports/sales.pdf", "oresto-caisses.pdf")}
            className="flex items-center justify-between p-3 bg-surface-muted rounded-xl hover:bg-[#ece8da] transition-colors text-left">
            <span className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-primary-c shrink-0" />
              <span className="truncate">{tx("ui_plus.k84ee42")}</span>
            </span>
            <span className="text-xs text-primary-c font-semibold shrink-0 ml-2">{tx("ui_plus.k294cb2")}</span>
          </button>
          <button data-testid="dl-invoices-pdf" onClick={() => dl("/reports/invoices.pdf", "oresto-factures.pdf")}
            className="flex items-center justify-between p-3 bg-surface-muted rounded-xl hover:bg-[#ece8da] transition-colors text-left">
            <span className="flex items-center gap-3 min-w-0">
              <FileText className="w-5 h-5 text-primary-c shrink-0" />
              <span className="truncate">{tx("ui_plus.k89691e")}</span>
            </span>
            <span className="text-xs text-primary-c font-semibold shrink-0 ml-2">{tx("ui_plus.k294cb2")}</span>
          </button>
          <button data-testid="dl-entries-xlsx" onClick={() => dl("/reports/entries.xlsx", "oresto-ecritures.xlsx")}
            className="flex items-center justify-between p-3 bg-surface-muted rounded-xl hover:bg-[#ece8da] transition-colors text-left">
            <span className="flex items-center gap-3 min-w-0">
              <FileSpreadsheet className="w-5 h-5 text-secondary-c shrink-0" />
              <span className="truncate">{tx("ui_plus.k49af41")}</span>
            </span>
            <span className="text-xs text-secondary-c font-semibold shrink-0 ml-2">{tx("ui_plus.k294cb2")}</span>
          </button>
          <button data-testid="dl-entries-csv" onClick={() => dl("/reports/entries.csv", "oresto-ecritures.csv")}
            className="flex items-center justify-between p-3 bg-surface-muted rounded-xl hover:bg-[#ece8da] transition-colors text-left">
            <span className="flex items-center gap-3 min-w-0">
              <FileSpreadsheet className="w-5 h-5 text-secondary-c shrink-0" />
              <span className="truncate">{tx("ui_plus.ka2d8e3")}</span>
            </span>
            <span className="text-xs text-secondary-c font-semibold shrink-0 ml-2">{tx("ui_plus.k294cb2")}</span>
          </button>
          <button data-testid="dl-sales-csv" onClick={() => dl("/reports/sales.csv", "oresto-caisses.csv")}
            className="flex items-center justify-between p-3 bg-surface-muted rounded-xl hover:bg-[#ece8da] transition-colors text-left">
            <span className="flex items-center gap-3 min-w-0">
              <FileSpreadsheet className="w-5 h-5 text-secondary-c shrink-0" />
              <span className="truncate">{tx("ui_plus.kd9c7f3")}</span>
            </span>
            <span className="text-xs text-secondary-c font-semibold shrink-0 ml-2">{tx("ui_plus.k294cb2")}</span>
          </button>
          <button data-testid="dl-invoices-csv" onClick={() => dl("/reports/invoices.csv", "oresto-factures.csv")}
            className="flex items-center justify-between p-3 bg-surface-muted rounded-xl hover:bg-[#ece8da] transition-colors text-left">
            <span className="flex items-center gap-3 min-w-0">
              <FileSpreadsheet className="w-5 h-5 text-secondary-c shrink-0" />
              <span className="truncate">{tx("ui_plus.k7a4c4e")}</span>
            </span>
            <span className="text-xs text-secondary-c font-semibold shrink-0 ml-2">{tx("ui_plus.k294cb2")}</span>
          </button>
        </div>
      </div>

      <MonthlyReportCard />
      <FiduciaryAccessCard defaultEmail={user?.establishment?.fiduciary_email} />

      <EstablishmentCard />
      <ClosuresCard />

      <button data-testid="edit-establishment-link" onClick={() => navigate("/onboarding")} className="card-c w-full flex items-center justify-between text-left">
        <span className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-primary-c" />
          <span>
            <p className="font-semibold">{tx("ui_plus.k43f05f")}</p>
            <p className="text-xs text-text-secondary">{tx("ui_plus.ke1554c")}</p>
          </span>
        </span>
        <span className="text-text-secondary">→</span>
      </button>

      {FEATURES.bank && (
      <button data-testid="banque-link" onClick={() => navigate("/app/banque")} className="card-c w-full flex items-center justify-between text-left">
        <span className="flex items-center gap-3">
          <Landmark className="w-5 h-5 text-primary-c" />
          <span>
            <p className="font-semibold">{tx("ui_plus.ka67b1d")}</p>
            <p className="text-xs text-text-secondary">{tx("ui_plus.k1278c6")}</p>
          </span>
        </span>
        <span className="text-text-secondary">→</span>
      </button>
      )}

      <PushToggle />

      <AccountDataCard />

      <button data-testid="logout-button" onClick={doLogout} className="card-c w-full flex items-center justify-between text-left">
        <span className="flex items-center gap-3">
          <LogOut className="w-5 h-5 text-red-600" />
          <span className="font-semibold text-red-600">{tx("ui_plus.kdca496")}</span>
        </span>
      </button>

      <p className="text-center text-xs text-text-secondary">{user?.email}</p>

      <nav className="legal-mini-links pb-4" data-testid="app-legal-links">
        <Link to="/cgv">{tx("ui_plus.k53fe50")}</Link>
        <Link to="/confidentialite">{tx("ui_plus.k3db25c")}</Link>
        <Link to="/registre-traitements">{tx("ui_plus.k98546e")}</Link>
        <Link to="/mentions-legales">{tx("ui_plus.k6ba684")}</Link>
      </nav>
    </div>
  );
}
